import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8093/api';

const client = axios.create({ baseURL: API_BASE_URL });

client.interceptors.request.use(config => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Types ─────────────────────────────────────────────────────────────────────

export interface StrategyItem {
  id:       number;
  code:     string;
  name:     string;
  isCustom: boolean;
}

export interface JournalTrade {
  id:             number;
  symbol:         string;
  exchange:       string;
  instrumentType: string;
  strategyId:     number;
  strategyCode:   string;
  strategyName:   string;
  entryMode:      string;
  tradeType:      string;
  direction:      string;
  entryDate:      string;
  exitDate:       string;
  quantity:       number | null;
  entryPrice:     number | null;
  exitPrice:      number | null;
  capitalUsed:    number | null;
  endCapital:     number | null;
  pnlAmount:      number;
  pnlPercent:     number | null;
  status:         string;
  imageUrl:       string | null;
  notes:          string | null;
  createdAt:      string;
}

export interface AddTradeReq {
  symbol:         string;
  exchange:       string;
  instrumentType: string;
  strategyId:     number;
  entryMode:      string;
  tradeType:      string;
  direction:      string;
  entryDate:      string;
  exitDate:       string;
  quantity:       number | null;
  entryPrice:     number | null;
  exitPrice:      number | null;
  capitalUsed:    number | null;
  endCapital:     number | null;
  pnlAmount:      number;
  pnlPercent:     number | null;
  notes:          string | null;
}

export interface CreateStrategyReq {
  code: string;
  name: string;
}

export interface StockSymbol {
  symbol: string;
  name:   string;
}

export interface TradeStats {
  totalTrades:   number;
  winningTrades: number;
  losingTrades:  number;
  winRate:       number;        // trade-level %
  avgWin:        number | null;
  avgLoss:       number | null;
  profitFactor:  number | null; // null when no losing trades
  bestTrade:     number | null; // single best trade P&L
  worstTrade:    number | null; // single worst trade P&L
}

// ── API calls ─────────────────────────────────────────────────────────────────

const journalApi = {
  getStrategies: () =>
    client.get<{ data: StrategyItem[] }>('/v1/journal/strategies'),

  createStrategy: (req: CreateStrategyReq) =>
    client.post<{ data: StrategyItem }>('/v1/journal/strategies', req),

  /** Pass month (1-12) to get monthly summary, omit for full year. */
  getSummary: (year: number, month?: number) =>
    client.get<{ data: Record<string, number> }>('/v1/journal/summary', {
      params: { year, ...(month != null ? { month } : {}) },
    }),

  getTradesForDate: (date: string) =>
    client.get<{ data: JournalTrade[] }>('/v1/journal/trades', { params: { date } }),

  addTrade: (req: AddTradeReq) =>
    client.post<{ data: JournalTrade }>('/v1/journal/trades', req),

  deleteTrade: (id: number) =>
    client.delete(`/v1/journal/trades/${id}`),

  /** Trade-level yearly stats: total trades, win rate, avg win/loss, profit factor. */
  getStats: (year: number) =>
    client.get<{ data: TradeStats }>('/v1/journal/stats', { params: { year } }),

  getStocks: (exchange = 'NSE') =>
    client.get<{ data: StockSymbol[] }>('/stocks', { params: { exchange } }),

  bulkUpload: (file: File, strategyId: number) => {
    const form = new FormData();
    form.append('file', file);
    form.append('strategyId', String(strategyId));
    return client.post<{ data: { saved: number; total: number } }>(
      '/v1/journal/trades/bulk', form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
};

export default journalApi;
